<template>
  <a-modal
      :width="460"
      :open="open"
      :footer="null"
      class="modal-no-padding survey-settings-modal"
      :wrap-class-name="isMobile ? 'ant-modal-bottom-wrap' : ''"
      @cancel="closeForm"
  >
    <template #title>
      <div class="modal-no-padding__header">
        <div class="modal-header">Настройки опроса «{{ survey?.title }}»</div>
      </div>
      <a-divider style="margin: 16px 0" />
    </template>

    <div class="modal-no-padding__body survey-settings-modal__body">
      <div class="survey-settings-modal__switch-row">
        <a-switch v-model:checked="form.active" />
        <span class="survey-settings-modal__switch-label">Активность опроса</span>
      </div>

      <div class="survey-settings-modal__form-fields">
        <div class="survey-settings-modal__field">
          <div class="survey-settings-modal__label">Кто участвует</div>
          <a-select
              v-model:value="form.groups"
              :placeholder="'Все сотрудники'"
              :not-found-content="'Нет данных'"
              :loading="groupsLoading"
              style="width: 100%"
              mode="multiple"
              show-arrow
              @change="handleGroupsChange"
          >
            <a-select-option v-for="item in audienceOptions" :key="item.id" :value="item.id">
              {{ item.text }}
            </a-select-option>
          </a-select>
        </div>

        <div class="survey-settings-modal__field">
          <div class="survey-settings-modal__label">Дата</div>
          <div class="survey-settings-modal__date-picker-control">
            <button
                type="button"
                class="survey-settings-modal__date-field"
                @click="openDatePicker"
            >
              <span>{{ selectedDateLabel }}</span>
              <CalendarOutlined class="survey-settings-modal__date-icon" />
            </button>
            <a-date-picker
                v-model:value="form.selectedDate"
                v-model:open="datePickerOpen"
                class="survey-settings-modal__hidden-date-picker"
                format="DD.MM.YYYY"
                :allow-clear="false"
                :disabled-date="isCalendarDateDisabled"
                :default-picker-value="datePickerDefaultValue"
                @change="handleDatePickerChange"
            />
          </div>
          <a-alert
              type="info"
              class="blue-alert survey-settings-modal__calendar-alert"
              :message="calendarInfoText"
          />
        </div>

        <div class="survey-settings-modal__row">
          <div class="survey-settings-modal__field survey-settings-modal__field--half">
            <div class="survey-settings-modal__label">Время Мск</div>
            <a-time-picker
                v-model:value="form.time"
                format="HH:mm"
                :show-now="false"
                :minute-step="5"
                :allow-clear="false"
                :placeholder="'12:00'"
                style="width: 100%"
            />
          </div>
        </div>
      </div>

      <a-alert
          type="info"
          class="blue-alert survey-settings-modal__alert"
          :message="frequencyText"
      />
    </div>

    <a-divider style="margin: 16px 0" />

    <div class="modal-no-padding__footer survey-settings-modal__footer">
      <a-button class="secondary-button" @click="closeForm">Отменить</a-button>
      <a-button class="primary-button" :loading="isProcessing" @click="handleSave">
        Сохранить
      </a-button>
    </div>
  </a-modal>
</template>

<script>
import { computed, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { message } from 'ant-design-vue';
import { CalendarOutlined } from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const DEFAULT_WEEKDAY = 'friday';
const DEFAULT_TIME = '12:00';
const DATE_FORMAT = 'YYYY-MM-DD';
const DISPLAY_DATE_FORMAT = 'DD.MM.YYYY';

const WEEKDAY_BY_INDEX = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export default {
  name: 'survey-settings-modal',
  components: { CalendarOutlined },
  emits: ['close', 'submitted'],
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    survey: {
      type: Object,
      default: null,
    },
    groupList: {
      type: Array,
      default: () => [],
    },
    groupsLoading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, context) {
    const store = useStore();

    const isProcessing = ref(false);
    const isMobile = ref(window.innerWidth <= 768);

    const allGroups = computed(() => props.groupList ?? []);

    const audienceOptions = computed(() => [
      { id: 'all', text: 'Все сотрудники' },
      ...allGroups.value.map((group) => ({
        id: group.id,
        text: group.name,
      })),
    ]);

    const form = reactive({
      active: true,
      groups: ['all'],
      weekday: DEFAULT_WEEKDAY,
      time: dayjs(DEFAULT_TIME, 'HH:mm'),
      selectedDate: null,
    });

    const datePickerOpen = ref(false);

    const frequencyText = computed(() => props.survey?.frequencyText ?? '');

    const getGroupId = (group) => (group && typeof group === 'object' ? group.id : group);

    const parseDate = (value) => {
      if (!value) {
        return null;
      }

      if (typeof value === 'string') {
        const parsedByFormat = [DATE_FORMAT, DISPLAY_DATE_FORMAT]
            .map((format) => dayjs(value, format, true))
            .find((date) => date.isValid());

        if (parsedByFormat) {
          return parsedByFormat.startOf('day');
        }
      }

      const parsed = dayjs(value);

      return parsed.isValid() ? parsed.startOf('day') : null;
    };

    const getSelectedDateFromSettings = (settings) => {
      return parseDate(settings.custom_intervals?.[0]);
    };

    const isFreeCalendarSelection = computed(() => {
      const settings = props.survey?.settings ?? {};

      return !settings.has_custom_settings || settings.first_usage !== false;
    });

    const baselineDate = computed(() => {
      const settings = props.survey?.settings ?? {};

      return parseDate(settings.last_survey_date);
    });

    const calendarWindow = computed(() => {
      const today = dayjs().startOf('day');

      if (isFreeCalendarSelection.value || !baselineDate.value) {
        return {
          start: today,
          end: null,
        };
      }

      const period = props.survey?.settings?.survey?.period ?? 'weekly';
      const baseline = baselineDate.value.startOf('day');

      switch (period) {
        case 'weekly':
          return {
            start: baseline,
            end: baseline.add(6, 'day'),
          };
        case 'monthly':
          return {
            start: baseline.add(3, 'week'),
            end: baseline.add(5, 'week'),
          };
        case 'quarterly':
          return {
            start: baseline.add(3, 'month').subtract(15, 'day'),
            end: baseline.add(3, 'month').add(15, 'day'),
          };
        default:
          return {
            start: today,
            end: null,
          };
      }
    });

    const formatDisplayDate = (date) => {
      return date ? date.format(DISPLAY_DATE_FORMAT) : '';
    };

    const calendarInfoText = computed(() => {
      if (isFreeCalendarSelection.value || !baselineDate.value) {
        return 'Выберите любую дату, начиная с сегодняшнего дня.';
      }

      const { start, end } = calendarWindow.value;

      return `Доступный период: ${formatDisplayDate(start)} - ${formatDisplayDate(end)}. Прошлая дата: ${formatDisplayDate(baselineDate.value)}.`;
    });

    const selectedDateLabel = computed(() => {
      return form.selectedDate ? formatDisplayDate(form.selectedDate) : 'Выбрать дату';
    });

    const datePickerDefaultValue = computed(() => {
      return form.selectedDate ?? calendarWindow.value.start ?? dayjs();
    });

    const isCalendarDateDisabled = (current) => {
      if (!current) {
        return false;
      }

      const date = current.startOf('day');
      const { start, end } = calendarWindow.value;

      if (start && date.isBefore(start, 'day')) {
        return true;
      }

      return Boolean(end && date.isAfter(end, 'day'));
    };

    const resetForm = () => {
      const settings = props.survey?.settings ?? {};
      const selectedDate = getSelectedDateFromSettings(settings);

      form.active = settings.active ?? true;
      form.groups = Array.isArray(settings.groups) ? settings.groups.map((group) => getGroupId(group)) : ['all'];
      form.weekday = settings.day_of_week ?? DEFAULT_WEEKDAY;
      form.time = settings.time ? dayjs(settings.time, 'HH:mm') : dayjs(DEFAULT_TIME, 'HH:mm');
      form.selectedDate = selectedDate;
      datePickerOpen.value = false;
    };

    const closeForm = () => {
      context.emit('close');
    };

    const handleGroupsChange = (value) => {
      if (!value.length || value[value.length - 1] === 'all') {
        form.groups = ['all'];
        return;
      }

      form.groups = value.filter((item) => item !== 'all');
    };

    const openDatePicker = () => {
      datePickerOpen.value = true;
    };

    const handleDatePickerChange = (value) => {
      if (!value) {
        return;
      }

      if (isCalendarDateDisabled(value)) {
        message.info(calendarInfoText.value);
        return;
      }

      const selectedDate = value.startOf('day');

      form.selectedDate = selectedDate;
      form.weekday = WEEKDAY_BY_INDEX[selectedDate.day()] ?? DEFAULT_WEEKDAY;
      datePickerOpen.value = false;
    };

    const handleSave = () => {
      const selectedDate = form.selectedDate;

      if (selectedDate && isCalendarDateDisabled(selectedDate)) {
        message.warning(calendarInfoText.value);
        return;
      }

      isProcessing.value = true;

      const payload = {
        periodic_survey_id: props.survey?.id,
        active: form.active,
        day_of_week: selectedDate ? WEEKDAY_BY_INDEX[selectedDate.day()] : form.weekday,
        time: form.time ? form.time.format('HH:mm') : DEFAULT_TIME,
        groups: form.groups.includes('all') ? 'all' : form.groups,
        custom_intervals: selectedDate ? [selectedDate.format(DATE_FORMAT)] : [],
      };

      store.dispatch('periodicSurveys/setSettings', payload).then((data) => {
        message.success('Настройки опроса сохранены');
        context.emit('submitted', data);
        closeForm();
      }).finally(() => {
        isProcessing.value = false;
      });
    };

    const onResize = () => {
      isMobile.value = window.innerWidth <= 768;
    };

    onBeforeMount(() => {
      resetForm();
    });

    onMounted(() => {
      window.addEventListener('resize', onResize);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', onResize);
    });

    watch(
        () => props.survey,
        () => {
          resetForm();
        },
        { deep: true }
    );

    watch(
        () => props.open,
        (value) => {
          if (value) {
            resetForm();
          }
        }
    );

    return {
      form,
      datePickerOpen,
      audienceOptions,
      frequencyText,
      calendarInfoText,
      selectedDateLabel,
      datePickerDefaultValue,
      isProcessing,
      isMobile,
      isCalendarDateDisabled,
      closeForm,
      handleGroupsChange,
      openDatePicker,
      handleDatePickerChange,
      handleSave,
    };
  },
};
</script>

<style scoped lang="scss">
.blue-alert.ant-alert {
  padding: 8px 12px;
  font-weight: 400;
  background-color: var(--geek-blue-1);
  border: none;
  border-radius: 4px;
}

.blue-alert .ant-alert-icon {
  display: none;
}

.blue-alert .ant-alert-message {
  padding: 0;
  margin: 0;
}

.survey-settings-modal__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.survey-settings-modal__form-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.survey-settings-modal__switch-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.survey-settings-modal__switch-label {
  font-size: 14px;
  color: var(--main-font);
}

.survey-settings-modal__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.survey-settings-modal__label {
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.45);
}

.survey-settings-modal__row {
  display: flex;
  gap: 12px;

  @media (max-width: $mobile-breakpoint) {
    flex-direction: column;
  }
}

.survey-settings-modal__field--half {
  flex: 1 1 0;
  min-width: 0;
}

.survey-settings-modal__calendar-alert {
  margin-top: 4px;
}

.survey-settings-modal__date-picker-control {
  position: relative;
  width: 100%;
}

.survey-settings-modal__date-field {
  width: 100%;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  color: var(--main-font);
  text-align: left;
  background: #fff;
  border: 1px solid var(--border-grey);
  border-radius: 4px;
  cursor: pointer;
}

.survey-settings-modal__date-icon {
  flex-shrink: 0;
  font-size: 18px;
  color: rgba(0, 0, 0, 0.35);
}

.survey-settings-modal__hidden-date-picker {
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
}

.survey-settings-modal__alert {
  margin: 0;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
}

.survey-settings-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;

  @media (max-width: $mobile-breakpoint) {
    flex-direction: column;

    .ant-btn {
      width: 100%;
    }
  }
}

.modal-header {
  padding-right: 20px;
}
</style>
